"use client";

/**
 * Agenda View
 * The Agenda layout, in one of three modes set on the schedule block:
 * - every day stacked (default): accent day heading, the day's summary, then its items as rows;
 * - day summaries only: one row per day with its summary, for a high-level agenda without times;
 * - one day at a time: a card with the Fetch-style day stepper, opening on today when it is an
 *   event day.
 * Untimed items with no date are listed last under "Anytime".
 */

import type React from "react";
import { useEffect, useMemo, useState } from "react";

import type { ScheduleDaySummary } from "../../../../_types";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { buildAgendaDays } from "../../../../_logic/utils/schedule-agenda";
import { selectDefaultDay } from "../../../../_logic/utils/schedule-utils";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { AgendaDayBlock } from "./AgendaDayBlock";
import { AgendaDayCard } from "./AgendaDayCard";
import { AgendaSummaryList } from "./AgendaSummaryList";
import { ScheduleRow, type ScheduleRowConfig } from "./ScheduleRow";

interface AgendaViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for the detail modal). */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone. NULL = floating time (no live "now"). */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
  /** The block's day summaries. */
  daySummaries?: ScheduleDaySummary[];
  /** Show only the days and their summaries. */
  isSummariesOnly?: boolean;
  /** Show one day at a time with a day stepper. */
  isOneDayAtATime?: boolean;
  /** Whether a label filter is narrowing the items (summary-only days are hidden while it is). */
  isFiltered?: boolean;
}

/**
 * Renders the Agenda layout.
 *
 * @param props - See AgendaViewProps.
 * @returns The agenda, or null when there is nothing to show.
 */
export function AgendaView({
  dateGroups,
  labels,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
  daySummaries,
  isSummariesOnly = false,
  isOneDayAtATime = false,
  isFiltered = false,
}: AgendaViewProps): React.ReactElement | null {
  // Hooks
  // Today (event zone, else viewer zone), resolved on mount to keep SSR markup stable.
  const todayStr = useEventToday(timeZone);

  // States: null until a day is picked or today resolves; the first day shows meanwhile.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [didInitToday, setDidInitToday] = useState(false);

  // Derived values
  const days = useMemo(
    () => buildAgendaDays(dateGroups, daySummaries, { includeSummaryOnlyDays: !isFiltered }),
    [dateGroups, daySummaries, isFiltered]
  );
  const anytimeItems = useMemo(() => dateGroups.find((g) => g.date === null)?.items ?? [], [dateGroups]);
  const dates = useMemo(() => days.map((day) => day.date), [days]);
  const surface = isOneDayAtATime ? "card" : "section";
  const rowConfig = useMemo<ScheduleRowConfig>(
    () => ({ labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations, surface }),
    [labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations, surface]
  );

  const selectedDay = days.find((day) => day.date === selectedDate) ?? days[0];

  // Effects: once today resolves (after mount), open on it when it is an event day. Runs once.
  useEffect(() => {
    if (didInitToday || todayStr == null) return;
    setSelectedDate(selectDefaultDay(dates, todayStr));
    setDidInitToday(true);
  }, [didInitToday, todayStr, dates]);

  // Render
  if (isSummariesOnly) return <AgendaSummaryList days={days} isPublic={isPublic} />;

  const anytimeList = anytimeItems.length > 0 && (
    <section className="flex flex-col gap-4" aria-label="Anytime">
      <h3
        className={`text-xl font-bold tracking-tight ${isOneDayAtATime ? "text-gray-900" : "text-(--lp-accent,#4338ca)"}`}
      >
        Anytime
      </h3>
      <ul
        className={`flex list-none flex-col divide-y border-y ${
          isOneDayAtATime
            ? "divide-gray-100 border-gray-100"
            : "divide-current/10 border-current/10 text-(--lp-text,#171717)"
        }`}
      >
        {anytimeItems.map((item) => (
          <li key={item.id}>
            <ScheduleRow config={rowConfig} item={item} />
          </li>
        ))}
      </ul>
    </section>
  );

  if (isOneDayAtATime) {
    if (!selectedDay && !anytimeList) return null;
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        {selectedDay && (
          <AgendaDayCard
            days={days}
            selectedDay={selectedDay}
            onSelectDate={setSelectedDate}
            todayStr={todayStr}
            rowConfig={rowConfig}
            timeZone={timeZone}
          />
        )}
        {anytimeList && (
          <div className="rounded-2xl border border-gray-200 bg-white px-5 pt-5 sm:px-7">{anytimeList}</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-14">
      {days.map((day) => (
        <AgendaDayBlock
          key={day.date}
          day={day}
          rowConfig={rowConfig}
          isToday={day.date === todayStr}
          timeZone={timeZone}
        />
      ))}
      {anytimeList}
    </div>
  );
}
